import type { AddressValidationState } from "@/types";
import type { GeocodedLocationValue } from "@/lib/geocodedLocation";
import { Eye, MapPin, Percent } from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  FieldErrors,
  UseFormRegister,
  UseFormRegisterReturn,
} from "react-hook-form";
import { EditableGeocodedLocation, SectionCard } from "@/components/common";
import { Label, PercentInput, Switch } from "@/components/ui";
import { formatCommissionPercentLabel } from "@/lib/commissionRate";
import type { House } from "@/types";
import type { HouseAdminDetailFormValues } from "../houseDetailForm";

interface HouseLocationSectionProps {
  value: GeocodedLocationValue;
  onChange: (next: GeocodedLocationValue) => void;
  error?: string;
  syncKey: string;
  modified: boolean;
  title: string;
  onValidationChange?: (state: AddressValidationState) => void;
}

export function HouseLocationSection({
  value,
  onChange,
  error,
  syncKey,
  modified,
  title,
  onValidationChange,
}: HouseLocationSectionProps) {
  const { t } = useTranslation(["houses", "common"]);

  const addressLabels = {
    search: t("common:addressSearch.label"),
    searchPlaceholder: t("common:addressSearch.placeholder"),
  };

  return (
    <SectionCard
      title={title}
      icon={MapPin}
      modified={modified}
      className="animate-slide-up"
    >
      <EditableGeocodedLocation
        value={value}
        onChange={onChange}
        labels={addressLabels}
        syncKey={syncKey}
        error={error}
        onValidationChange={onValidationChange}
      />
    </SectionCard>
  );
}

export function HouseCommissionReadOnlySection({ house }: { house: House }) {
  const { t } = useTranslation("houses");

  return (
    <SectionCard
      title={t("adminDetail.commissionSettings")}
      icon={Percent}
      description={t("detail.overview.hostShareHint")}
    >
      <p className="text-2xl font-semibold tracking-tight text-surface-900 tabular-nums">
        {formatCommissionPercentLabel(house.commission_rate)}
      </p>
    </SectionCard>
  );
}

interface HouseAdminCommissionSectionProps {
  register: UseFormRegister<HouseAdminDetailFormValues>;
  errors: FieldErrors<HouseAdminDetailFormValues>;
  modified: boolean;
}

export function HouseAdminCommissionSection({
  register,
  errors,
  modified,
}: HouseAdminCommissionSectionProps) {
  const { t } = useTranslation("houses");

  return (
    <SectionCard
      title={t("adminDetail.commissionSettings")}
      icon={Percent}
      modified={modified}
      description={t("detail.overview.hostShareHint")}
    >
      <Label htmlFor="house-commission">
        {t("adminDetail.commissionPct")}
        <span className="text-error-500"> *</span>
      </Label>
      <PercentInput
        id="house-commission"
        className="mt-1 max-w-xs"
        {...register("commission_percent")}
        required
        error={errors.commission_percent?.message}
        placeholder={t("adminDetail.commissionPlaceholder")}
      />
    </SectionCard>
  );
}

interface HouseVisibilitySectionProps {
  inputProps: UseFormRegisterReturn<"is_active">;
  modified: boolean;
}

/** Admin-only listing toggle (was in the header; rarely changed). */
export function HouseVisibilitySection({
  inputProps,
  modified,
}: HouseVisibilitySectionProps) {
  const { t } = useTranslation("houses");

  return (
    <SectionCard
      title={t("detail.settings.visibilityTitle")}
      icon={Eye}
      modified={modified}
    >
      <Switch
        {...inputProps}
        aria-label={t("detail.visibilityToggleAria")}
        label={t("detail.visibilityLabel")}
        description={t("detail.visibilityHint")}
      />
    </SectionCard>
  );
}
